import { getExtensibilityStore, getMultiAccountStore } from './store';
import type ExtensibilityState from './schema/ExtensibilityState';
import type TaskPaneRunningInstance from './schema/TaskPaneRunningInstance';
import type { ExtensibilityHostItem } from './schema/ExtensibilityHostItem';
import { initializeCompliance } from './schema/Compliance';
import type { ContextualCalloutState } from './schema/ContextualCalloutState';
import type RunningInstance from './schema/RunningInstance';
import type IExtendedAddinCommand from './schema/interfaces/IExtendedAddinCommand';
import type TaskPaneType from './schema/TaskPaneType';
import type { ActiveDialog } from './schema/interfaces/Dialog';
import type { SoftBlockDialogObservableState } from './schema/enums/SoftBlockOnSendConstants';
import type { OwaDate } from 'owa-datetime';
import { getAccountKeyForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { trace } from 'owa-trace';
import type { IEnabledAddinCommands } from 'owa-addins-types';
import type { Extension, AddInAcquisition } from 'owa-addins-contracts';
import { isAddinMultiAccountEnabled } from 'owa-feature-flags';
import type { ExtensibilityHostItemInstance } from './schema/ExtensibilityHostItemInstance';

export default function getExtensibilityState(mailboxInfo?: MailboxInfo): ExtensibilityState {
    if (!isAddinMultiAccountEnabled()) {
        return getExtensibilityStore();
    }
    if (!mailboxInfo) {
        trace.warn(
            '[ExtensibilityState] - No mailboxInfo provided, using global settings',
            'addins'
        );
        mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    }

    const key = getAccountKeyForMailboxInfo(mailboxInfo);
    let extState = getMultiAccountStore().get(key);
    if (!extState) {
        const defaultExtState = {
            /*
            Remove the typecast below while removing ts-expect in the EnabledAddinCommands object creation
            at owa-addins-store/src/store/store.ts
            */
            /* eslint-disable-next-line owa-custom-rules/no-recasting-null-undefined  -- (https://aka.ms/OWALintWiki)
             * Baseline; please fix or update justification when touching this code.
             *	> Do not cast null as another type. */
            EnabledAddinCommands: null as any as IEnabledAddinCommands,
            HostItems: new Map<string, ExtensibilityHostItem>(),
            IsPreinstalledTeachingUIAvailable: false,
            Context: null,
            taskPanes: new Map<string /*key: tabId*/, Map<TaskPaneType, TaskPaneRunningInstance>>(),
            activeDialogs: new Map<string /*key: tabId*/, ActiveDialog>(),
            ExtensibilityStateIsDirty: false,
            contextualCalloutState: new Map<string /*key: tabId*/, ContextualCalloutState>(),
            runningContextualAddinCommand: new Map<string /*key: tabId*/, RunningInstance>(),
            runningUILessExtendedAddinCommands: new Map<
                string /*key: tabId*/,
                Map<string, IExtendedAddinCommand>
            >(),
            autoRunAddinCommandWaitingQueue: new Map<
                string /*key: tabId*/,
                Array<IExtendedAddinCommand>
            >(),
            autoRunAddinCommandWaitingQueueV2: new Map<
                string /*key: tabId*/,
                Map<string, Array<IExtendedAddinCommand>>
            >(),
            compliance: initializeCompliance(),
            activeAutorunUilessFrames: new Map<string, any>(),
            currentSoftBlockDialogState: new Map<
                string /*key: tabId*/,
                SoftBlockDialogObservableState
            >(),
            telemetryLoggedInfo: new Map<string, Map<string, OwaDate>>(),
            addinsMap: new Map<string, Extension | AddInAcquisition>(),
            multiSelectEnabledAddinsCommand: [],
            innerPersistedAddins: {},
            HostItemInstance: new Map<string, ExtensibilityHostItemInstance>(),
            isOnSendPromptUserAddinInstalled: false,
            onSendAddinsCompletedExecutionForHostItemIndex: new Map<string, string[]>(), // key: hostItemIndex, value: addinId[]
            hasAppDeeplink: false,
            autorunReadAddinInternetHeaders: new Map<string, string>(),
            initialDataCalledForControlId: new Map<string, Map<string, boolean>>(),
        };

        getMultiAccountStore().set(key, defaultExtState);
        extState = getMultiAccountStore().get(key) as ExtensibilityState;
        trace.info(
            'getExtensibilityState for identity' +
                mailboxInfo.userIdentity +
                ' is undefined, creating. Total states:' +
                getMultiAccountStore().size
        );
    }
    return extState;
}
